import React, { useEffect, useState, useRef, useCallback } from 'react';
import Lang from 'src/libraries/language/en';
import { mapValues, groupBy } from 'lodash';
import classnames from 'classnames';
import {
  ATMHeader,
  ATMForm,
  ATMGrid,
  ATMField,
  ATMLoader,
  ATMButton,
  ATMTextArea,
  ATMSelect,
  ATMDivider,
  ATMSegment,
} from 'shared-it-appmod-ui';
import {
  ToastError,
  ToastSuccess,
} from 'src/components/atoms/toaster/toaster.component';
import {
  RiskAssessmentFormSchema,
  IRiskAssessmentFormSchema,
} from 'src/models/risk-assessment.model';
import { IRiskAssessmentKeySchema } from 'src/models/risk-assessment-key.model';
import { useRiskAssessmentContext } from 'src/contexts/risk-assessment.context';
import { useRiskAssessmentKeyContext } from 'src/contexts/risk-assessment-key.context';
import history from 'src/history';
import { riskMeter } from 'src/libraries/common.library';
import styles from './risk-assessment.module.scss';

const formatOptions = (data: IRiskAssessmentKeySchema[]) => {
  return data.map((item) => ({
    section: item.section,
    key: item.fieldKey,
    value: item.fieldKey,
    text: `${item.fieldName} (${item.fieldValue})`,
  }));
};

const defaultValues: IRiskAssessmentFormSchema = {
  picExperienceInModal: '',
  consecDaysOnDuty: '',
  dutyDay: '',
  dutyDayWOCL: '',
  picCurrencyInMission: '',
  picExperienceInMission: '',
  ceilingVisibility: '',
  densityAltitude: '',
  windsInMissionArea: '',
  terrain: '',
  timeOfDay: '',
  missionType: '',
  landingZone: '',
  mitigationDesc: '',
};

export type IProps = {
  requestId: number;
};

export const calculateScore = (data, assessmentList) => {
  if (!data) {
    return 0;
  }

  return Object.entries(data)
    .filter(([key]) => !['riskScore', 'mitigationDesc'].includes(key))
    .map(
      ([_, item]) =>
        assessmentList.find((risk) => risk.fieldKey === item)?.fieldValue ?? 0
    )
    .reduce((a: number, b: number) => a + b, 0);
};

const RiskAssessment: React.FC<IProps> = ({ requestId }) => {
  const { state, actions } = useRiskAssessmentContext();
  const {
    state: riskAssessmentKeyState,
    actions: riskAssessmentKeyActions,
  } = useRiskAssessmentKeyContext();

  const formRef = useRef<HTMLFormElement>(null);
  const [count, setCount] = useState<number>(0);
  const [ready, setReady] = useState<boolean>(false);
  const [isSubmit, setIsSubmit] = useState<boolean>(false);
  const [riskScore, setRiskScore] = useState<number>(0);

  useEffect(() => {
    const fetch = async () => {
      await riskAssessmentKeyActions.listGET();
      await actions.dataGET(requestId);

      setReady(true);

      setCount((prevCount) => prevCount + 1);
    };

    fetch();
  }, [requestId, actions, riskAssessmentKeyActions, setReady, setCount]);

  useEffect(() => {
    const riskSum = calculateScore(state.data, riskAssessmentKeyState.list);
    setRiskScore(riskSum);
  }, [state.data]);

  const handleSetFlag = ([, { name, value }], control) => {
    control.setValue(name, value);
    const riskSum = calculateScore(
      control.getValues(),
      riskAssessmentKeyState.list
    );
    setRiskScore(riskSum);
    return value;
  };

  const handleCancel = () => {
    history.goBack();
  };

  const handleSave = useCallback(
    (isExit = false) => {
      if (formRef && formRef.current) {
        formRef.current.handleSubmit(isExit);
      }
    },
    [formRef]
  );

  const riskKey = mapValues(
    groupBy(formatOptions(riskAssessmentKeyState.list), 'section')
  );

  const onSubmit = useCallback(
    async (data, isExit) => {
      if (!isExit) {
        setIsSubmit(true);
      }

      let message: string;
      let response;

      const params = {
        ...data,
        ...{ requestId },
      };

      const check = await actions.dataGET(requestId);

      if (!check?.payload?.riskAssessment) {
        response = await actions.addPost(params);
        message = Lang.MSG_ADD_RISK_ASSESSMENT_SUCCESS;
      } else {
        response = await actions.updatePut(params);
        message = Lang.MSG_UPDATE_RISK_ASSESSMENT_SUCCESS;
      }

      if (!response.error) {
        ToastSuccess(message);
      } else {
        ToastError(response.error.message ?? Lang.MSG_SOMETHING_WENT_WRONG);
      }

      if (!isExit) {
        setIsSubmit(false);
      } else {
        handleCancel();
      }

      await actions.dataGET(requestId);
    },
    [actions]
  );

  return !ready ? (
    <ATMLoader active size="medium" />
  ) : (
    <>
      <ATMHeader>
        {Lang.TTL_RISK_ASSESSMENT}
        <ATMHeader.Subheader>
          {Lang.TTL_RISK_ASSESSMENT_FLIGHT_NO} {requestId}
        </ATMHeader.Subheader>
      </ATMHeader>

      <ATMSegment>
        <ATMForm
          key={`${requestId}-${count}`}
          ref={formRef}
          mode="onChange"
          validationSchema={RiskAssessmentFormSchema}
          onSubmit={onSubmit}
          defaultValue={defaultValues}
          className={styles.container}
        >
          {({ control, errors }) => {
            return (
              <div>
                <ATMGrid columns={3} divided="vertically">
                  <ATMGrid.Column>
                    <ATMGrid.Row>
                      <ATMHeader as="h2">{Lang.LBL_PILOT}</ATMHeader>
                      <ATMGrid.Column>
                        <ATMField
                          name="picExperienceInModal"
                          as={ATMSelect}
                          selectOnBlur={false}
                          className={styles.field}
                          control={control}
                          error={errors?.picExperienceInModal}
                          placeholder={Lang.LBL_SELECT}
                          label={Lang.LBL_ASSESSMENT_PIC_EXPERIENCE_IN_MODAL}
                          defaultValue={state?.data?.picExperienceInModal}
                          onChange={(e) => handleSetFlag(e, control)}
                          options={riskKey.picExperienceInModal}
                        />
                      </ATMGrid.Column>
                    </ATMGrid.Row>
                    <ATMGrid.Row>
                      <ATMGrid.Column>
                        <ATMField
                          name="picExperienceInMission"
                          as={ATMSelect}
                          selectOnBlur={false}
                          className={styles.field}
                          control={control}
                          error={errors?.picExperienceInMission}
                          placeholder={Lang.LBL_SELECT}
                          label={Lang.LBL_ASSESSMENT_PIC_EXPERIENCE_MISSION}
                          defaultValue={state?.data?.picExperienceInMission}
                          onChange={(e) => handleSetFlag(e, control)}
                          options={riskKey.picExperienceInMission}
                        />
                      </ATMGrid.Column>
                    </ATMGrid.Row>
                    <ATMGrid.Row>
                      <ATMGrid.Column>
                        <ATMField
                          name="picCurrencyInMission"
                          as={ATMSelect}
                          selectOnBlur={false}
                          className={styles.field}
                          control={control}
                          error={errors?.picCurrencyInMission}
                          placeholder={Lang.LBL_SELECT}
                          label={Lang.LBL_ASSESSMENT_PIC_CURRENCY_MISSION}
                          defaultValue={state?.data?.picCurrencyInMission}
                          onChange={(e) => handleSetFlag(e, control)}
                          options={riskKey.picCurrencyInMission}
                        />
                      </ATMGrid.Column>
                    </ATMGrid.Row>

                    <ATMGrid.Row>
                      <ATMGrid.Column>
                        <ATMField
                          name="consecDaysOnDuty"
                          as={ATMSelect}
                          selectOnBlur={false}
                          className={styles.field}
                          control={control}
                          error={errors?.consecDaysOnDuty}
                          placeholder={Lang.LBL_SELECT}
                          label={Lang.LBL_ASSESSMENT_CONSEC_DAYS_ON_DUTY}
                          defaultValue={state?.data?.consecDaysOnDuty}
                          onChange={(e) => handleSetFlag(e, control)}
                          options={riskKey.consecDaysOnDuty}
                        />
                      </ATMGrid.Column>
                    </ATMGrid.Row>
                    <ATMGrid.Row>
                      <ATMGrid.Column>
                        <ATMField
                          name="dutyDay"
                          as={ATMSelect}
                          selectOnBlur={false}
                          className={styles.field}
                          control={control}
                          error={errors?.dutyDay}
                          placeholder={Lang.LBL_SELECT}
                          label={Lang.LBL_ASSESSMENT_DUTY_DAY}
                          defaultValue={state?.data?.dutyDay}
                          onChange={(e) => handleSetFlag(e, control)}
                          options={riskKey.dutyDay}
                        />
                      </ATMGrid.Column>
                    </ATMGrid.Row>

                    <ATMGrid.Row>
                      <ATMGrid.Column>
                        <ATMField
                          name="dutyDayWOCL"
                          as={ATMSelect}
                          selectOnBlur={false}
                          clearable
                          className={styles.field}
                          control={control}
                          error={errors?.dutyDayWOCL}
                          placeholder={Lang.LBL_SELECT}
                          label={Lang.LBL_ASSESSMENT_DUTY_DAY_WOCL}
                          defaultValue={state?.data?.dutyDayWOCL}
                          onChange={(e) => handleSetFlag(e, control)}
                          options={riskKey.dutyDayWOCL}
                        />
                      </ATMGrid.Column>
                    </ATMGrid.Row>
                  </ATMGrid.Column>
                  <ATMGrid.Column>
                    <ATMGrid.Row>
                      <ATMHeader as="h2">{Lang.LBL_ENVIRONMENT}</ATMHeader>
                      <ATMGrid.Column>
                        <ATMField
                          name="timeOfDay"
                          as={ATMSelect}
                          selectOnBlur={false}
                          className={styles.field}
                          control={control}
                          error={errors?.timeOfDay}
                          placeholder={Lang.LBL_SELECT}
                          label={Lang.LBL_ASSESSMENT_TIME_OF_DAY}
                          defaultValue={state?.data?.timeOfDay}
                          onChange={(e) => handleSetFlag(e, control)}
                          options={riskKey.timeOfDay}
                        />
                      </ATMGrid.Column>
                    </ATMGrid.Row>
                    <ATMGrid.Row>
                      <ATMGrid.Column>
                        <ATMField
                          name="ceilingVisibility"
                          as={ATMSelect}
                          selectOnBlur={false}
                          className={styles.field}
                          control={control}
                          error={errors?.ceilingVisibility}
                          placeholder={Lang.LBL_SELECT}
                          label={Lang.LBL_ASSESSMENT_CEILING_VISIBILITY}
                          defaultValue={state?.data?.ceilingVisibility}
                          onChange={(e) => handleSetFlag(e, control)}
                          options={riskKey.ceilingVisibility}
                        />
                      </ATMGrid.Column>
                    </ATMGrid.Row>

                    <ATMGrid.Row>
                      <ATMGrid.Column>
                        <ATMField
                          name="windsInMissionArea"
                          as={ATMSelect}
                          selectOnBlur={false}
                          className={styles.field}
                          control={control}
                          error={errors?.windsInMissionArea}
                          placeholder={Lang.LBL_SELECT}
                          label={Lang.LBL_ASSESSMENT_WINDS_IN_MISSION_AREA}
                          defaultValue={state?.data?.windsInMissionArea}
                          onChange={(e) => handleSetFlag(e, control)}
                          options={riskKey.windsInMissionArea}
                        />
                      </ATMGrid.Column>
                    </ATMGrid.Row>

                    <ATMGrid.Row>
                      <ATMGrid.Column>
                        <ATMField
                          name="densityAltitude"
                          as={ATMSelect}
                          selectOnBlur={false}
                          className={styles.field}
                          control={control}
                          error={errors?.densityAltitude}
                          placeholder={Lang.LBL_SELECT}
                          label={Lang.LBL_ASSESSMENT_DENSITY_ALTITUDE}
                          defaultValue={state?.data?.densityAltitude}
                          onChange={(e) => handleSetFlag(e, control)}
                          options={riskKey.densityAltitude}
                        />
                      </ATMGrid.Column>
                    </ATMGrid.Row>

                    <ATMGrid.Row>
                      <ATMGrid.Column>
                        <ATMField
                          name="terrain"
                          as={ATMSelect}
                          selectOnBlur={false}
                          className={styles.field}
                          control={control}
                          error={errors?.terrain}
                          placeholder={Lang.LBL_SELECT}
                          label={Lang.LBL_ASSESSMENT_TERRAIN}
                          defaultValue={state?.data?.terrain}
                          onChange={(e) => handleSetFlag(e, control)}
                          options={riskKey.terrain}
                        />
                      </ATMGrid.Column>
                    </ATMGrid.Row>
                  </ATMGrid.Column>

                  <ATMGrid.Column>
                    <ATMGrid.Row>
                      <ATMHeader as="h2">{Lang.LBL_MISSION}</ATMHeader>
                      <ATMGrid.Column>
                        <ATMField
                          name="missionType"
                          as={ATMSelect}
                          selectOnBlur={false}
                          className={styles.field}
                          control={control}
                          error={errors?.missionType}
                          placeholder={Lang.LBL_SELECT}
                          label={Lang.LBL_ASSESSMENT_MISSION_TYPE}
                          defaultValue={state?.data?.missionType}
                          onChange={(e) => handleSetFlag(e, control)}
                          options={riskKey.missionType}
                        />
                      </ATMGrid.Column>
                    </ATMGrid.Row>

                    <ATMGrid.Row>
                      <ATMGrid.Column>
                        <ATMField
                          name="landingZone"
                          as={ATMSelect}
                          selectOnBlur={false}
                          className={styles.field}
                          control={control}
                          error={errors?.landingZone}
                          placeholder={Lang.LBL_SELECT}
                          label={Lang.LBL_ASSESSMENT_LANDING_ZONE}
                          defaultValue={state?.data?.landingZone}
                          onChange={(e) => handleSetFlag(e, control)}
                          options={riskKey.landingZone}
                        />
                      </ATMGrid.Column>
                    </ATMGrid.Row>
                    <ATMGrid.Row>
                      <ATMGrid.Column>
                        <div className={styles.scoreWrapper}>
                          <p>
                            Total Risk Score:{' '}
                            <span className={styles.bold}>
                              {
                                Lang.RISK_DETAIL[
                                  riskMeter(riskScore).replace('-risk', '')
                                ]
                              }
                            </span>
                          </p>
                          <span
                            className={classnames(
                              riskMeter(riskScore),
                              styles.score
                            )}
                          >
                            {riskScore}
                          </span>
                          <p className={styles.scoreExplanation}>
                            10-24 LOW,&nbsp;&nbsp;25-49 MODERATE,&nbsp;&nbsp;50+
                            *HIGH <br /> *Requires Chief Pilot and ASD
                            notification
                          </p>
                        </div>
                      </ATMGrid.Column>
                    </ATMGrid.Row>
                  </ATMGrid.Column>
                </ATMGrid>

                <ATMDivider />

                <ATMGrid columns={2} divided="vertically">
                  <ATMGrid.Column>
                    <ATMGrid.Row>
                      <ATMGrid>
                        <ATMGrid.Column>
                          <ATMHeader as="h3">
                            {Lang.LBL_ASSESSMENT_ADDITIONAL}
                          </ATMHeader>
                          <ATMGrid.Row>{Lang.LBL_ASSESSMENT_A}</ATMGrid.Row>
                          <ATMGrid.Row>{Lang.LBL_ASSESSMENT_B}</ATMGrid.Row>
                          <ATMGrid.Row>{Lang.LBL_ASSESSMENT_C}</ATMGrid.Row>
                          <ATMGrid.Row>{Lang.LBL_ASSESSMENT_D}</ATMGrid.Row>
                          <ATMGrid.Row>{Lang.LBL_ASSESSMENT_E}</ATMGrid.Row>
                          <ATMGrid.Row>{Lang.LBL_ASSESSMENT_F}</ATMGrid.Row>
                          <ATMGrid.Row>{Lang.LBL_ASSESSMENT_G}</ATMGrid.Row>
                          <ATMGrid.Row>{Lang.LBL_ASSESSMENT_H}</ATMGrid.Row>
                          <ATMGrid.Row> {Lang.LBL_ASSESSMENT_I}</ATMGrid.Row>
                        </ATMGrid.Column>
                      </ATMGrid>
                    </ATMGrid.Row>
                  </ATMGrid.Column>
                  <ATMGrid.Column>
                    <ATMGrid.Row>
                      <ATMField
                        name="mitigationDesc"
                        as={ATMTextArea}
                        control={control}
                        placeholder="1000 character limit"
                        label="**Briefly state mitigations:"
                        rows={10}
                        error={errors?.mitigationDesc}
                        defaultValue={state?.data?.mitigationDesc}
                      />
                    </ATMGrid.Row>
                  </ATMGrid.Column>
                </ATMGrid>
              </div>
            );
          }}
        </ATMForm>
      </ATMSegment>

      <div className={styles.buttonsWrapper}>
        <ATMButton primary basic onClick={handleCancel}>
          {Lang.LBL_CANCEL}
        </ATMButton>
        <ATMButton primary onClick={() => handleSave(false)} loading={isSubmit}>
          {Lang.LBL_SAVE_EXIT}
        </ATMButton>
      </div>
    </>
  );
};

export default RiskAssessment;
